import { GetSearchParametersWithCountsResponse } from "@/app/shared/apis/top-deals";
import { FilterOptionView } from "./filter-option.view";
import { MakeModelFilterView, ModelFilterView } from "./make-model-filter.view";

export interface ProvinceBucket {
	readonly code: string;
	readonly name: string;
	readonly count: number;
}

export interface RegionBucket {
	readonly code: string;
	readonly name: string;
	readonly country: string;
	readonly count: number;
	readonly provinces: ProvinceBucket[];
}

type RegionsHierarchyShim = {
	regionsHierarchy?: RegionBucket[];
};

/**
 * Main view model for all search filters
 */
export class SearchFiltersView {
	private _makes: MakeModelFilterView[];
	private _suppliers: FilterOptionView[];
	private _fuelTypes: FilterOptionView[];
	private _transmissions: FilterOptionView[];
	private _saleTypes: FilterOptionView[];
	private _colors: FilterOptionView[];
	private _doors: FilterOptionView[];
	private _seats: FilterOptionView[];
	private _regionsByCountry: Map<string, RegionBucket[]>;

	constructor(response: GetSearchParametersWithCountsResponse) {
		this._makes = response.makes.map(
			make =>
				new MakeModelFilterView(
					make.name,
					make.count,
					make.models.map(
						model =>
							new ModelFilterView(
								model.name,
								model.count,
								model.name,
								model.trims.map(
									trim => new FilterOptionView(trim.name, trim.count, trim.name)
								)
							)
					)
				)
		);

		this._suppliers = response.suppliers.map(
			supplier =>
				new FilterOptionView(supplier.name, supplier.count, supplier.name)
		);

		this._fuelTypes = response.fuelTypes.map(
			fuelType =>
				new FilterOptionView(fuelType.name, fuelType.count, fuelType.name)
		);

		this._transmissions = response.transmissions.map(
			transmission =>
				new FilterOptionView(
					transmission.name,
					transmission.count,
					transmission.name
				)
		);

		this._saleTypes = response.saleTypes.map(
			saleType =>
				new FilterOptionView(saleType.name, saleType.count, saleType.name)
		);

		this._colors = response.colors.map(
			color => new FilterOptionView(color.name, color.count, color.name)
		);

		this._doors = response.doors.map(
			door => new FilterOptionView(door.name, door.count, door.name)
		);

		this._seats = response.seats.map(
			seat => new FilterOptionView(seat.name, seat.count, seat.name)
		);

		// V2 region hierarchy. Generated API client may not know this field yet
		// (depends on gen:api refresh after NestJS deploy), so cast via a local
		// shim. Only enriched listings contribute, so empty is valid.
		const hierarchy =
			(response as GetSearchParametersWithCountsResponse & RegionsHierarchyShim)
				.regionsHierarchy ?? [];

		this._regionsByCountry = new Map();
		for (const region of hierarchy) {
			const list = this._regionsByCountry.get(region.country) ?? [];
			list.push(region);
			this._regionsByCountry.set(region.country, list);
		}
	}

	get makes(): MakeModelFilterView[] {
		return this._makes;
	}

	get suppliers(): FilterOptionView[] {
		return this._suppliers;
	}

	get fuelTypes(): FilterOptionView[] {
		return this._fuelTypes;
	}

	get transmissions(): FilterOptionView[] {
		return this._transmissions;
	}

	get saleTypes(): FilterOptionView[] {
		return this._saleTypes;
	}

	get colors(): FilterOptionView[] {
		return this._colors;
	}

	get doors(): FilterOptionView[] {
		return this._doors;
	}

	get seats(): FilterOptionView[] {
		return this._seats;
	}

	/**
	 * Regions grouped by country code (e.g. "ES", "FR"). Each region carries
	 * provinces[] for the drill-in. Sorted by count desc upstream.
	 */
	get regionsByCountry(): Map<string, RegionBucket[]> {
		return this._regionsByCountry;
	}

	get hasRegions(): boolean {
		return this._regionsByCountry.size > 0;
	}
}
